import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSounds from '../hooks/useSounds';
import Logo from '../components/Logo';
import {
  IELTSExamDuration,
  IELTSWritingTask,
  IELTSWritingEvaluation,
  WRITING_PROMPTS,
} from '../types/ielts';

interface LocationState {
  duration: IELTSExamDuration;
}

// Minimum words required for proper scoring (below this = score 1 for all criteria)
const MIN_WORDS_FOR_SCORING = 20;

// Helper to round to nearest whole number (for component scores)
const roundToWhole = (num: number): number => {
  return Math.round(num);
};

// IELTS band score rounding: .25 and .75 round DOWN
// e.g., 6.25 -> 6.0, 6.5 -> 6.5, 6.75 -> 6.5, 7.0 -> 7.0
// With 4 integer component scores, possible decimals are only: .00, .25, .50, .75
const roundIELTSBand = (num: number): number => {
  const decimal = num - Math.floor(num);
  // .00 and .25 round to .0; .50 and .75 round to .5
  if (decimal < 0.5) {
    return Math.floor(num);
  } else {
    return Math.floor(num) + 0.5;
  }
};

// Extended evaluation type with per-task scores
interface ExtendedWritingEvaluation extends IELTSWritingEvaluation {
  task1Scores?: {
    taskAchievement: number;
    coherenceAndCohesion: number;
    lexicalResource: number;
    grammaticalRange: number;
    overallBand: number;
  };
  task2Scores?: {
    taskAchievement: number;
    coherenceAndCohesion: number;
    lexicalResource: number;
    grammaticalRange: number;
    overallBand: number;
  };
}

const IELTSWritingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playSound } = useSounds();

  const state = location.state as LocationState;
  const duration = state?.duration || 'short';

  // Determine which tasks to include based on duration
  const getTasks = (): IELTSWritingTask[] => {
    const task1 = WRITING_PROMPTS.task1[Math.floor(Math.random() * WRITING_PROMPTS.task1.length)];
    const task2 = WRITING_PROMPTS.task2[Math.floor(Math.random() * WRITING_PROMPTS.task2.length)];

    switch (duration) {
      case 'short':
        return [task1];
      case 'medium':
        return [task2];
      case 'full':
        return [task1, task2];
      default:
        return [task1];
    }
  };

  // Get total time based on duration mode
  const getTotalTime = (): number => {
    switch (duration) {
      case 'short':
        return 30 * 60; // 30 minutes for Task 1 only
      case 'medium':
        return 45 * 60; // 45 minutes for Task 2 only
      case 'full':
        return 60 * 60; // 60 minutes shared for full test
      default:
        return 30 * 60;
    }
  };

  const [tasks] = useState<IELTSWritingTask[]>(getTasks);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [responses, setResponses] = useState<{ task: number; text: string; wordCount: number }[]>(
    tasks.map(t => ({ task: t.task, text: '', wordCount: 0 }))
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(getTotalTime());
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [evaluation, setEvaluation] = useState<ExtendedWritingEvaluation | null>(null);
  const [status, setStatus] = useState<'intro' | 'in-progress' | 'completed'>('intro');

  // Use refs for timer to work independently of tab visibility
  const startTimeRef = useRef<number | null>(null);
  const totalTimeRef = useRef<number>(getTotalTime());

  const currentTask = tasks[currentTaskIndex];
  const currentResponse = responses[currentTaskIndex];

  // Block copy/paste/cut during the test
  useEffect(() => {
    const preventCopyPaste = (e: ClipboardEvent) => {
      if (status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    const preventContextMenu = (e: MouseEvent) => {
      if (status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('contextmenu', preventContextMenu);

    return () => {
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [status]);

  // Timer effect - uses real time elapsed instead of intervals
  // This ensures timer continues even when tab is inactive
  useEffect(() => {
    if (!isTimerRunning) return;

    const updateTimer = () => {
      if (startTimeRef.current === null) return;

      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, totalTimeRef.current - elapsed);

      setTimeRemaining(remaining);

      if (remaining === 0) {
        playSound('complete');
        handleSubmit();
      }
    };

    // Update immediately and then every second
    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    // Also update when tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerRunning]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const wordCount = countWords(text);

    setResponses(prev => {
      const updated = [...prev];
      updated[currentTaskIndex] = { ...updated[currentTaskIndex], text, wordCount };
      return updated;
    });
  };

  const handleStart = () => {
    setStatus('in-progress');
    setIsTimerRunning(true);
    startTimeRef.current = Date.now();
    playSound('startRecording');
  };

  // Switch between tasks (for full test mode)
  const handleSwitchTask = (index: number) => {
    setCurrentTaskIndex(index);
  };

  // Calculate scores for a single task response
  const calculateTaskScores = (wordCount: number, minWords: number) => {
    // If less than 20 words, all criteria get score 1
    if (wordCount < MIN_WORDS_FOR_SCORING) {
      return {
        taskAchievement: 1,
        coherenceAndCohesion: 1,
        lexicalResource: 1,
        grammaticalRange: 1,
        overallBand: 1,
      };
    }

    const completionRatio = Math.min(1, wordCount / minWords);
    const baseScore = Math.min(9, Math.max(4, completionRatio * 5 + 4));

    // Component scores must be whole numbers only (no .5)
    const taskAchievement = roundToWhole(Math.min(9, Math.max(1, baseScore + (Math.random() * 1 - 0.5))));
    const coherenceAndCohesion = roundToWhole(Math.min(9, Math.max(1, baseScore + (Math.random() * 1 - 0.5))));
    const lexicalResource = roundToWhole(Math.min(9, Math.max(1, baseScore + (Math.random() * 1 - 0.5))));
    const grammaticalRange = roundToWhole(Math.min(9, Math.max(1, baseScore + (Math.random() * 1 - 0.5))));

    // Calculate overall band as average, using IELTS rounding rules (.25/.75 round down)
    const avg = (taskAchievement + coherenceAndCohesion + lexicalResource + grammaticalRange) / 4;
    const overallBand = roundIELTSBand(avg);

    return {
      taskAchievement,
      coherenceAndCohesion,
      lexicalResource,
      grammaticalRange,
      overallBand,
    };
  };

  const handleSubmit = useCallback(async () => {
    setIsTimerRunning(false);
    setStatus('completed');

    // Calculate scores per task
    const taskScores = responses.map((response, index) => {
      const task = tasks[index];
      return calculateTaskScores(response.wordCount, task.wordCount.min);
    });

    let finalEvaluation: ExtendedWritingEvaluation;

    if (duration === 'full' && taskScores.length === 2) {
      // Full test: Task 1 = 1/3, Task 2 = 2/3 weight
      const task1Scores = taskScores[0];
      const task2Scores = taskScores[1];

      // Weighted average for each criterion (round to whole numbers)
      const taskAchievement = roundToWhole((task1Scores.taskAchievement * 1 + task2Scores.taskAchievement * 2) / 3);
      const coherenceAndCohesion = roundToWhole((task1Scores.coherenceAndCohesion * 1 + task2Scores.coherenceAndCohesion * 2) / 3);
      const lexicalResource = roundToWhole((task1Scores.lexicalResource * 1 + task2Scores.lexicalResource * 2) / 3);
      const grammaticalRange = roundToWhole((task1Scores.grammaticalRange * 1 + task2Scores.grammaticalRange * 2) / 3);

      // Overall band uses IELTS rounding (.25/.75 round down)
      const overallBand = roundIELTSBand((taskAchievement + coherenceAndCohesion + lexicalResource + grammaticalRange) / 4);

      const avgWordCount = responses.reduce((sum, r) => sum + r.wordCount, 0) / responses.length;

      finalEvaluation = {
        overallBand,
        taskAchievement,
        coherenceAndCohesion,
        lexicalResource,
        grammaticalRange,
        task1Scores,
        task2Scores,
        feedback: `Your writing demonstrates a ${overallBand >= 7 ? 'good' : 'developing'} level of English. ${
          avgWordCount >= 200
            ? 'You met the word count requirements.'
            : 'Consider writing more to fully develop your ideas.'
        }`,
        strengths: [
          'Attempted all required tasks',
          avgWordCount >= 150 ? 'Good attempt at meeting word count' : 'Made an effort to respond',
          'Addressed the topic',
        ],
        improvements: [
          'Develop arguments with more specific examples',
          'Use a wider range of vocabulary',
          'Vary sentence structures for better coherence',
          'Check grammar and punctuation carefully',
        ],
      };
    } else {
      // Single task
      const scores = taskScores[0];
      const response = responses[0];
      const task = tasks[0];

      finalEvaluation = {
        overallBand: scores.overallBand,
        taskAchievement: scores.taskAchievement,
        coherenceAndCohesion: scores.coherenceAndCohesion,
        lexicalResource: scores.lexicalResource,
        grammaticalRange: scores.grammaticalRange,
        feedback: `Your writing demonstrates a ${scores.overallBand >= 7 ? 'good' : 'developing'} level of English. ${
          response.wordCount >= task.wordCount.min
            ? 'You met the word count requirements.'
            : 'Consider writing more to fully develop your ideas.'
        }`,
        strengths: [
          'Attempted the required task',
          response.wordCount >= task.wordCount.min * 0.8 ? 'Good attempt at meeting word count' : 'Made an effort to respond',
          'Addressed the topic',
        ],
        improvements: [
          'Develop arguments with more specific examples',
          'Use a wider range of vocabulary',
          'Vary sentence structures for better coherence',
          'Check grammar and punctuation carefully',
        ],
      };
    }

    setEvaluation(finalEvaluation);
    playSound('success');
  }, [responses, tasks, duration, playSound]);

  if (status === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">IELTS Writing Test</h1>
            <p className="text-gray-600">
              {duration === 'short' && 'Quick Practice - Task 1 Only (over 150 words) - 30 minutes'}
              {duration === 'medium' && 'Standard Practice - Task 2 Only (over 250 words) - 45 minutes'}
              {duration === 'full' && 'Full Exam - Both Tasks - 60 minutes total'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Test Overview</h2>
            <div className="space-y-4">
              {tasks.map((task, index) => (
                <div key={task.task} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800">Task {task.task}: {task.title}</h3>
                    <p className="text-sm text-gray-600">{task.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {task.wordCount.min}+ words
                      {duration !== 'full' && ` • ${task.timeMinutes} minutes`}
                    </p>
                  </div>
                </div>
              ))}
              {duration === 'full' && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> You have 60 minutes total for both tasks. You can freely switch between Task 1 and Task 2 at any time during the test.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-emerald-50 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-emerald-800 mb-2">Tips for Success</h3>
            <ul className="text-sm text-emerald-700 space-y-1">
              <li>- Read the prompt carefully before writing</li>
              <li>- Plan your response for 2-3 minutes</li>
              <li>- Use paragraphs to organize your ideas</li>
              <li>- Leave time to check your work</li>
              <li>- Stay within the time limit</li>
            </ul>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/app')}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center gap-2"
              title="Go to Home"
            >
              <Logo size="sm" />
            </button>
            <button
              onClick={() => navigate('/app/ielts')}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Back to IELTS
            </button>
            <button
              onClick={handleStart}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              Begin Test
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'completed' && evaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Test Complete!</h1>
            <p className="text-gray-600">Here's your IELTS Writing evaluation</p>
          </div>

          {/* Overall Band Score */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6 text-center">
            <p className="text-gray-600 mb-2">Overall Band Score</p>
            <div className="text-6xl font-bold text-emerald-600 mb-2">{evaluation.overallBand.toFixed(1)}</div>
            {duration === 'full' && (
              <p className="text-sm text-gray-500">
                (Task 1 contributes 1/3, Task 2 contributes 2/3)
              </p>
            )}
          </div>

          {/* Individual Task Scores (for full test) */}
          {duration === 'full' && evaluation.task1Scores && evaluation.task2Scores && (
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">Task 1 Scores</h3>
                <div className="text-center mb-3">
                  <span className="text-3xl font-bold text-emerald-600">{evaluation.task1Scores.overallBand.toFixed(1)}</span>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Task Achievement', score: evaluation.task1Scores.taskAchievement },
                    { label: 'Coherence & Cohesion', score: evaluation.task1Scores.coherenceAndCohesion },
                    { label: 'Lexical Resource', score: evaluation.task1Scores.lexicalResource },
                    { label: 'Grammatical Range', score: evaluation.task1Scores.grammaticalRange },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between items-center bg-gray-50 rounded px-3 py-2">
                      <span className="text-xs text-gray-600">{item.label}</span>
                      <span className="font-bold text-gray-800">{item.score.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">Task 2 Scores</h3>
                <div className="text-center mb-3">
                  <span className="text-3xl font-bold text-emerald-600">{evaluation.task2Scores.overallBand.toFixed(1)}</span>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Task Achievement', score: evaluation.task2Scores.taskAchievement },
                    { label: 'Coherence & Cohesion', score: evaluation.task2Scores.coherenceAndCohesion },
                    { label: 'Lexical Resource', score: evaluation.task2Scores.lexicalResource },
                    { label: 'Grammatical Range', score: evaluation.task2Scores.grammaticalRange },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between items-center bg-gray-50 rounded px-3 py-2">
                      <span className="text-xs text-gray-600">{item.label}</span>
                      <span className="font-bold text-gray-800">{item.score.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Combined Detailed Scores */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {duration === 'full' ? 'Combined Weighted Scores' : 'Detailed Scores'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Task Achievement', score: evaluation.taskAchievement },
                { label: 'Coherence & Cohesion', score: evaluation.coherenceAndCohesion },
                { label: 'Lexical Resource', score: evaluation.lexicalResource },
                { label: 'Grammatical Range', score: evaluation.grammaticalRange },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">{item.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{item.score.toFixed(1)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Your Responses */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Your Responses</h2>
            {responses.map((response, index) => (
              <div key={index} className="mb-4 last:mb-0">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-700">Task {tasks[index].task}</h3>
                  <span className={`text-sm font-medium ${
                    response.wordCount < MIN_WORDS_FOR_SCORING
                      ? 'text-red-600'
                      : response.wordCount >= tasks[index].wordCount.min
                        ? 'text-emerald-600'
                        : 'text-gray-500'
                  }`}>
                    {response.wordCount} words
                    {response.wordCount < MIN_WORDS_FOR_SCORING && ' (below minimum)'}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {response.text || '(No response submitted)'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Feedback */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Feedback</h2>
            <p className="text-gray-700 mb-4">{evaluation.feedback}</p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="font-medium text-green-800 mb-2">Strengths</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  {evaluation.strengths.map((s, i) => (
                    <li key={i}>+ {s}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <h3 className="font-medium text-amber-800 mb-2">Areas to Improve</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  {evaluation.improvements.map((s, i) => (
                    <li key={i}>- {s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/app')}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center gap-2"
              title="Go to Home"
            >
              <Logo size="sm" />
            </button>
            <button
              onClick={() => navigate('/app/ielts')}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Back to IELTS
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // In-progress state
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header with Timer */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 sticky top-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-800">
                Task {currentTask.task}: {currentTask.title}
              </h2>
              {duration === 'full' ? (
                <p className="text-sm text-gray-500">
                  Full test - switch freely between tasks
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  {currentTaskIndex + 1} of {tasks.length} tasks
                </p>
              )}
            </div>
            <div className="text-right">
              <div
                className={`text-2xl font-mono font-bold ${
                  timeRemaining < 60 ? 'text-red-600 animate-pulse' : timeRemaining < 300 ? 'text-red-600' : 'text-emerald-600'
                }`}
              >
                {formatTime(timeRemaining)}
              </div>
              <p className="text-xs text-gray-500">Time remaining</p>
            </div>
          </div>

          {/* Task Switcher for Full Test Mode */}
          {duration === 'full' && tasks.length > 1 && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
              {tasks.map((task, index) => (
                <button
                  key={task.task}
                  onClick={() => handleSwitchTask(index)}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    currentTaskIndex === index
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Task {task.task}
                  <span className={`ml-2 text-xs ${currentTaskIndex === index ? 'text-emerald-100' : 'text-gray-500'}`}>
                    ({responses[index].wordCount} words)
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Task Prompt */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">Task {currentTask.task}</h3>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-gray-700 whitespace-pre-wrap">{currentTask.prompt}</p>
          </div>
          <div className="flex gap-4 text-sm text-gray-500">
            <span>Minimum: {currentTask.wordCount.min} words</span>
            {duration !== 'full' && <span>Time: {currentTask.timeMinutes} minutes</span>}
          </div>
        </div>

        {/* Writing Area */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-800">Your Response</h3>
            <span
              className={`text-sm font-medium ${
                currentResponse.wordCount < MIN_WORDS_FOR_SCORING
                  ? 'text-red-600'
                  : currentResponse.wordCount >= currentTask.wordCount.min
                    ? 'text-emerald-600'
                    : 'text-gray-500'
              }`}
            >
              {currentResponse.wordCount} / {currentTask.wordCount.min}+ words
              {currentResponse.wordCount < MIN_WORDS_FOR_SCORING && (
                <span className="ml-1 text-xs">(min 20 required)</span>
              )}
            </span>
          </div>
          <textarea
            value={currentResponse.text}
            onChange={handleTextChange}
            placeholder="Start writing your response here..."
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            autoComplete="off"
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            className="w-full h-96 p-4 border border-gray-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-gray-800"
            />
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
                navigate('/app/ielts');
              }
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Exit Test
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
            >
              Submit Test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IELTSWritingPage;
